/* eslint-disable sort-keys */
import {
  alpha,
  Grid,
  Typography
} from "@mui/material";
import { LinkProps } from "@mui/material/Link";
import {
  createTheme,
  PaletteOptions,
  SimplePaletteColorOptions,
  styled
} from "@mui/material/styles";
import jsonColors from "./envColors.json";
import { LinkBehavior } from "../components";

interface DefaultPaletteOptions extends PaletteOptions {
  primary?: SimplePaletteColorOptions;
  secondary?: SimplePaletteColorOptions;
}

const defaultColors = {
  backgroundAccent: "#0FD9FF",
  backgroundCard: "#1B1B1B",
  backgroundDisabled: "#C3C3C3",
  backgroundDisabledAlternative: "#4A4A4A",
  backgroundError: "#FFCFC8",
  backgroundPrimary: "#1B1B1B",
  backgroundPrimaryCta: "#0FD9FF",
  backgroundPrimaryCtaHover: "#0C95AF",
  backgroundSecondaryCtaHover: "#4A4A4A",
  backgroundPrimaryLighter: "#151515",
  backgroundPrimaryLighterAlternative: "#151515",
  backgroundSaved: "#FFFF00",
  backgroundSecondary: "#FFFFFF",
  backgroundSecondaryAlternative: "#D4F8FF",
  backgroundSecondaryComplete: "#EEFFCD",
  backgroundSecondaryHover: "#ffffff1a",
  // backgroundSecondaryHover: "#ffffff1a", //TODO ripristinare
  backgroundWarning: "#FFF2D9",
  backgroundChatBotContainer: "#151515",
  backgroundFooter: "#151515",
  backgroundPrimaryControlDisabled: "105, 105, 105, 0.7",
  // backgroundPrimaryControlDisabled: "#696969B2",
  backgroundPrimaryControl: "#2A2A2A",
  backgroundPrimaryControlHover: "#1A1A1A",
  border: "#696969",
  borderAccent: "#D9D9D9",
  borderError: "#B61E06",
  borderPrimaryCta: "#0FD9FF",
  borderPrimaryCtaHover: "#098299",
  borderPrimaryControl: "#4A4A4A",
  borderPrimaryControlHover: "#3A3A3A",
  borderPrimaryControlDisabled: "74, 74, 74, 0.7", //TODO mettere hex
  borderSecondaryComplete: "#A7FF03",
  borderTag: "#FFFFFF",
  borderWarning: "#FFC03E",
  "button-secondary-text": "#FFFFFF",

  textDisabled: "#696969",
  textDisabledAlternative: "#C3C3C3",
  textError: "#FF6B00",
  textInput: "#151515",
  textMandatory: "#0FD9FF",
  textPrimary: "#FFFFFF",
  textPrimaryControl: "#FFFFFF",
  textPrimaryAlternative: "#151515",
  textPrimaryCta: "#1B1B1B",
  textPrimaryControlDisabled: "250, 250, 250, 0.7",
  // textPrimaryControlDisabled: "#fafafab3", //TODO ripristinare
  textTag: "#323232",
  textWarning: "#FF6B00",

  systemDisabled: "#D9D9D9",
  systemPrimary01: "#FFFFFF",
  systemPrimary01Alternative02: "#FFFFFF",
  systemPrimary02: "#F43E63",
  systemSecondary01: "#0FD9FF",
  systemSecondary01Hover: "#00B6D8",
  systemSecondary05: "#151515",
  systemSecondary03: "#FF6B00",

  accentVariantA: "#0FD9FF", // bitterLemon
  accentVariantB: "#A7FF03", // vividSkyBlue
  accentVariantC: "#FF6B00", //springOrange06
  accentVariantD: "#0FD9FF",
  accentVariantE: "#FFFFFF",
  drawerWidgetBg: "#1B1B1B",


  inputBackground: "#292929",
  inputBorder: "#696969",
  exploreCardText: "#FFFFFF",
  textWidget: "#FFFFFF",
  textWidgetAlternative: "#151515",
  headerIcon: "#FFFFFF",
  textFooter: "#ffffff",

  //GRADIENTS
  gradient01Start: "25,25,31,1", //widget
  gradient01End: "62,62,67,1",

  gradient02Start: "27,27,27,0.3", //immagini preferiti, sfondo dettaglio corso, video brightcove
  gradient02End: "27,27,27,1",
  gradient03End: "27,27,27,0.7",

  gradient04Start: "27,27,27,0.4", // pagina profilo
  gradient04End: "27,27,27,1",

  gradient05Start: "27,27,27,0",
  gradient05End: "27,27,27,1",

  gradient06End: "27,27,27,0.6",

  gradient07Start: "68,68,68,1", // skeleton loader
  gradient07End: "27,27,27,1",

  gradient08Start: "27,27,27,0.50", // card carousel esplora, sia in hover che non + card in profile preferences
  gradient08End: "27,27,27,1",

  gradient09Start: "27,27,27,0.9",
  gradient10Start: "27,27,27,0.8"
};
const localStorageColors = localStorage.getItem("storageColors") as string;


const enviromentalColors = localStorageColors ? JSON.parse(localStorageColors) as Partial<typeof defaultColors> :
  jsonColors as Partial<typeof defaultColors>;

const customColors = Object.assign(defaultColors, enviromentalColors);
type CustomColors = typeof customColors

const linearGradients = {
  gradientB: `linear-gradient(180deg, rgba(${customColors.gradient01Start}) 0%,
    rgba(${customColors.gradient01End}) 100%)`,

  gradientC: `linear-gradient(180deg, rgba(${customColors.gradient02Start}) 0%,
    rgba(${customColors.gradient02End}) 100%)`,

  gradientDAlternative01: `linear-gradient(0deg, rgba(${customColors.gradient05Start}) 50%,
    rgba(${customColors.gradient05End}) 100%)`,

  gradientDAlternative02: `linear-gradient(0deg, rgba(${customColors.gradient05Start}) 70%,
    rgba(${customColors.gradient05End}) 100%)`,

  gradientDAlternative03: `linear-gradient(180deg, rgba(${customColors.gradient05Start}) 50%,
    rgba(${customColors.gradient05End}) 100%)`,

  gradientDAlternative04: `linear-gradient(180deg, rgba(${customColors.gradient05Start}) 20%,
    rgba(${customColors.gradient05End}) 40%)`,

  gradientDAlternative05: `linear-gradient(270deg, rgba(${customColors.gradient05Start}) 30%,
    rgba(${customColors.gradient05End}) 100%)`,

  gradientDAlternative06: `linear-gradient(270deg, rgba(${customColors.gradient05Start}) 0%,
    rgba(${customColors.gradient05End}) 100%)`,

  gradientDAlternative09: `linear-gradient(180deg, rgba(${customColors.gradient04Start}) 0%,
      rgba(${customColors.gradient04End}) 100%)`,

  gradientDAlternative10: `linear-gradient(90deg, rgba(${customColors.gradient07Start}) 27%,
        rgba(${customColors.gradient07End}) 100%)`,

  gradientDAlternative11: `linear-gradient(90deg, rgba(${customColors.gradient07End}) 27%,
        rgba(${customColors.gradient07Start}) 100%)`,

  gradientCarouselCard: `linear-gradient(180deg, rgba(${customColors.gradient08Start}) 0%,
   rgba(${customColors.gradient08End}) 100%)`,

  gradientCarouselCard90Deg: `linear-gradient(90deg, rgba(${customColors.gradient08Start}) 0%,
   rgba(${customColors.gradient08End}) 100%)`,

  gradientVideoPlayerLoader: `linear-gradient(0deg, rgba(${customColors.gradient02Start}) 50%,
   rgba(${customColors.gradient02End}) 100%), linear-gradient(180deg, rgba(${customColors.gradient02Start}) 50%,
   rgba(${customColors.gradient02End}) 100%)`
};
type LinearGradients = typeof linearGradients & Record<string, string>

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: false; // removes the `xl` breakpoint
    max: true; // adds the `max` breakpoint
  }

  interface Theme {
    customColors: CustomColors,
    linearGradients: LinearGradients,
    palette: DefaultPaletteOptions
  }

  // allow configuration using `createTheme`
  interface ThemeOptions {
    customColors: CustomColors,
    linearGradients: LinearGradients,
    palette?: PaletteOptions
  }
}

export const domain1 = createTheme({
  // cfr. https://mui.com/material-ui/customization
  breakpoints: {
    values: {
      /* eslint-disable sort-keys */
      xs: 450, //mobile
      sm: 769, //tablet-mobile
      md: 900,
      lg: 1200,
      max: 1440
      /* eslint-enable sort-keys */
    }
  },
  components: {
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backdropFilter: "blur(2px)",
          background: alpha(customColors.drawerWidgetBg, 0.25),
          boxShadow: " 0px 4px 4px rgba(0, 0, 0, 0.25)",

          "&.MuiBackdrop-invisible": {
            backdropFilter: "none",
            backgroundColor: "transparent",

            "&.backdrop-search": {
              backdropFilter: "blur(2px)",
              background: alpha(customColors.drawerWidgetBg, 0.25)
            }
          }
        }
      }
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
        LinkComponent: LinkBehavior
      }
    },
    MuiIconButton: {
      styleOverrides: { root: { minWidth: 24, minHeight: 24 } }
    },
    MuiCssBaseline: {
      styleOverrides: `
        body {
          font-family: "Inter";
          overscroll-behavior: none;

          a {
            color:${customColors.backgroundPrimaryCta};
            font-weight:500;
           };

          .mask {
            &::before {
              content:"";
              backdrop-filter: none;
              background:transparent;
              pointer-events:none;
              position:fixed;
              top:0;
              bottom:0;
              right:0;
              left:0;
              transition:all .3s linear;

            }
            &.has-mask {

              &::before  {
                content:"";
                backdrop-filter: blur(2px);
                background: ${alpha(customColors.drawerWidgetBg, 0.5)};
                box-shadow: ${alpha(customColors.drawerWidgetBg, 0.5)};
                pointer-events:auto;
                z-index:1200
              }

          }
          }

          .scrollable {
            overflow-y:scroll;

            &:hover {
              ::-webkit-scrollbar-thumb {
                background:${customColors.systemPrimary01};
              }
            };

            ::-webkit-scrollbar {
              width: 6px;
            };

            ::-webkit-scrollbar-thumb {
              background-color: transparent;
              border-radius: 8px;
            }
          }


          div#freshworks-container {
            width: fit-content;
            position:relative !important;
            z-index:1400 !important;
          };

          .asw-menu-btn {
            height: 40px !important;
            width: 40px !important;
            bottom:30px !important;
            left:30px !important;
            box-shadow: 0 0 10px rgba(255, 255, 255, 0.4) !important;
          }

          .asw-menu-btn: hover {
            transform: scale(1.1) !important;
          }

          .asw-menu-btn > svg {
            width: 25px !important;
            height: 25px !important;
            min-height: 25px !important;
            min-width: 25px !important;
          }

          @media (max-width: 768px) {
            div#freshworks-container iframe#launcher-frame {
              // bottom:60px !important;

              .launcher-button.hupbHS {
                background-color:red !important;
              }
            }

            .asw-menu-btn {
              bottom:80px !important;
            }
          }
        }
      `
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior
      } as LinkProps
    },

    MuiDrawer: {
      styleOverrides: {
        root: {

          "&.drawer-search-mobile": {
            zIndex: "1300"
          },

          "&.chatBot-drawer": {
            zIndex: 1400,

            ".MuiModal-backdrop": {
              zIndex: 1400
            },

            "@media (max-width: 599.95px)": {


              bottom: "56px",

              ".MuiModal-backdrop": {
                bottom: "77.83px"
              }

            }
          }


        }
      }
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          "zIndex": 2147483648,
          "&.popover-notification": {

            ".MuiPopover-paper": {
              borderBottomLeftRadius: "8px",
              borderBottomRightRadius: "8px",
              borderTop: 0,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0
            }
          },

          ".MuiPopover-paper": {
            background: customColors.backgroundPrimary,
            border: `1px solid ${customColors.border}`,
            borderRadius: "8px",

            ".MuiMenu-list": {
              padding: 0
            }
          },

          "&.popover-searchBar": {
            top: "88px",

            ".MuiModal-backdrop": {
              backdropFilter: "blur(2px)",
              border: "none",
              borderRadius: "0px",
              boxShadow: " 0px 4px 4px rgba(0, 0, 0, 0.25)",
              top: "88px"
            },

            ".MuiPopover-paper": {
              border: "none",
              borderRadius: "2px",
              top: "0px !important"
            }
          },

          "&.popover-community, &.popover-goals, &.popover-goals-success, &.popover-modal-history": {
            zIndex: "1400",

            ".MuiModal-backdrop": {

              backdropFilter: "blur(2px)",
              background: "rgba(255, 255, 255, 0.3)",
              border: "none",
              borderRadius: "0px",
              boxShadow: " 0px 4px 4px rgba(0, 0, 0, 0.25)"
            },
            ".MuiPopover-paper": {
              border: "none",
              borderRadius: "2px"
            }
          }
        }
      }
    }
  },
  customColors,
  linearGradients,
  palette: {
    background: {
      default: customColors.backgroundPrimary,
      paper: customColors.backgroundPrimaryLighter
    },
    divider: customColors.border,
    info: {
      main: customColors.textPrimary
    },
    primary: {
      main: customColors.systemPrimary02
    },
    secondary: {
      main: customColors.textMandatory
    },
    text: {
      primary: customColors.textPrimary,
      secondary: "#757575"
    }
  },
  typography: {
    fontFamily: "Inter"
  }
});

export const TypographyDescription = styled(Typography)(({ theme }) => ({
  color: customColors.textPrimary,
  fontSize: "1.25rem",
  fontWeight: "400",
  lineHeight: "1.5",
  padding: "0 0 24px",

  [theme.breakpoints.down("sm")]: {
    fontSize: "0.875rem",
    wordBreak: "break-word"
  }
}));

export const TypographyTitle = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontSize: "1.5rem",
  fontWeight: "700",
  lineHeight: "1.5",
  padding: " 0 0 24px",

  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem"
  }
}));

export const WrapperSmall = styled(Grid)(() => ({
  borderBottom: `1px solid  ${customColors.border}`,
  padding: "0px 6.25rem 4.875rem"

}));

